import { useState, useEffect } from "react";
import {
    Box,
    Badge,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import TableContainerWithScrollShadow from "components/TableContainerWithScrollShadow";
import { trackEvent } from "../../../configureMixpanel";

const FileStorageSchemasList = ({ projectId, api, onError }) => {
    const [schemas, setSchemas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedFileId, setSelectedFileId] = useState(null);
    const [selectedSchemaId, setSelectedSchemaId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const fetchSchemas = async () => {
        try {
            setIsLoading(true);
            const data = await api.fetchFileTemplates(projectId);
            setSchemas(data);
        } catch (error) {
            onError(error.message || 'Failed to fetch schemas')
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSchemas();
    }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const data = await api.fetchFileTemplates(projectId);
            setSchemas(data);
        } catch (error) {
            onError(error.message || 'Failed to refresh schemas')
        } finally {
            setRefreshing(false);
        }
    };

    const handleDeleteClick = (schemaId, fileId) => {
        setSelectedSchemaId(schemaId);
        setSelectedFileId(fileId);
        setDeleteDialogOpen(true);
    };

    const handleDownloadClick = async (schemaId, fileId, fileName) => {
        setDownloading(fileId);
        try {
            await api.downloadFile(schemaId, fileId, fileName);
            trackEvent("file_downloaded", {});
        } catch (error) {
            onError(error.message || 'Failed to download file.')
        } finally {
            setDownloading(null);
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedFileId || !selectedSchemaId) return;

        setDeleting(true);
        try {
            await api.deleteFile(selectedSchemaId, selectedFileId);
            trackEvent("file_deleted", {});

            // Refetch schemas after deletion
            const data = await api.fetchFileTemplates(projectId);
            setSchemas(data);

            setDeleteDialogOpen(false);
            setSelectedFileId(null);
            setSelectedSchemaId(null);
        } catch (error) {
            onError(error.message || 'failed to delete file.');
        } finally {
            setDeleting(false);
        }
    };

    if (isLoading) return <CircularProgress />;

    if (schemas.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography color="textSecondary">
                        No file templates or files found
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">File Templates</Typography>
                <IconButton
                    onClick={handleRefresh}
                    disabled={refreshing}
                    size="small"
                    title="Refresh schemas"
                    color="primary"
                >
                    {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
                </IconButton>
            </Box>
            {schemas.map((schema) => (
                <Accordion key={schema.id} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">{schema.name}</Typography>
                        {schema.files && schema.files.length > 0 && (
                            <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
                                ({schema.files.length} files)
                            </Typography>
                        )}
                    </AccordionSummary>
                    <AccordionDetails>
                        {!schema.files || schema.files.length === 0 ? (
                            <Typography color="textSecondary">No files uploaded</Typography>
                        ) : (
                            <TableContainerWithScrollShadow>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>File Name</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Uploaded At</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {schema.files.map((file) => (
                                            <TableRow key={file.id}>
                                                <TableCell>{file.name}</TableCell>
                                                <TableCell>
                                                    <Badge badgeContent={file.status} color="primary" />
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(file.created_at).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDownloadClick(schema.id, file.id, file.name)}
                                                        color="primary"
                                                        disabled={downloading === file.id}
                                                    >
                                                        {downloading === file.id ? (
                                                            <CircularProgress size={24} />
                                                        ) : (
                                                            <FileDownloadIcon />
                                                        )}
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteClick(schema.id, file.id)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainerWithScrollShadow>
                        )}
                    </AccordionDetails>
                </Accordion>
            ))}

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete File</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this file?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        disabled={deleting}
                    >
                        {deleting ? <CircularProgress size={24} /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FileStorageSchemasList;